import { create } from 'zustand';
import type { EditorState } from 'lexical';
import type { MessageEntity } from '@tgpe/shared';
import { MAX_MESSAGE_LENGTH, MAX_CAPTION_LENGTH } from '@tgpe/shared';
import type { PreviewState, PreviewTab, ContentMode } from '../types';

export interface MediaPreviewItem {
  id: string;
  thumbnailUrl: string | null;
  previewUrl: string | null;
  /** Playable source URL (blob: for current session, asset: for reloaded files) */
  objectUrl: string | null;
  type: 'photo' | 'video' | 'audio';
  isMissing: boolean;
  /** Original image/video width in pixels (for mosaic layout) */
  width: number;
  /** Original image/video height in pixels (for mosaic layout) */
  height: number;
  /** Audio duration in seconds */
  durationSeconds?: number;
  /** Audio performer/artist name */
  performer?: string;
  /** Audio track title */
  title?: string;
}

interface PreviewStoreState {
  /** Current preview state with text, entities, html, charCount */
  state: PreviewState | null;
  /** Current Lexical editor state for lazy exports */
  editorState: EditorState | null;
  /** Active tab in the format toggle */
  activeTab: PreviewTab;
  /** Content mode: message (4096 limit) or caption (dynamic limit) */
  mode: ContentMode;
  /** Caption character limit (varies by premium status) */
  captionLimit: number;
  /** Channel name to display in preview header */
  channelName: string;
  /** Whether the content exceeds the limit */
  isOverLimit: boolean;
  /** Media items for preview display */
  mediaPreview: MediaPreviewItem[];
  /** Whether link preview generation is enabled for the current draft */
  linkPreviewEnabled: boolean;
}

interface PreviewStoreActions {
  /** Update preview state from editor export */
  updatePreview: (
    text: string,
    entities: MessageEntity[],
    html: string,
    editorState: EditorState
  ) => void;
  /** Set the active tab */
  setActiveTab: (tab: PreviewTab) => void;
  /** Set the content mode */
  setMode: (mode: ContentMode) => void;
  /** Set the caption limit (for premium support) */
  setCaptionLimit: (limit: number) => void;
  /** Set the channel name */
  setChannelName: (name: string) => void;
  /** Set media preview items */
  setMediaPreview: (items: MediaPreviewItem[]) => void;
  /** Set link preview enabled state */
  setLinkPreviewEnabled: (enabled: boolean) => void;
  /** Clear the preview state */
  clear: () => void;
}

type PreviewStore = PreviewStoreState & PreviewStoreActions;

const DEFAULT_STATE: PreviewStoreState = {
  state: null,
  editorState: null,
  activeTab: 'preview',
  mode: 'message',
  captionLimit: MAX_CAPTION_LENGTH,
  channelName: 'Preview',
  isOverLimit: false,
  mediaPreview: [],
  linkPreviewEnabled: true,
};

export const usePreviewStore = create<PreviewStore>((set, get) => ({
  ...DEFAULT_STATE,

  updatePreview: (text, entities, html, editorState) => {
    const charCount = text.length; // UTF-16 code units
    const { mode, captionLimit } = get();
    const limit = mode === 'message' ? MAX_MESSAGE_LENGTH : captionLimit;

    set({
      state: {
        text,
        entities,
        html,
        charCount,
      },
      editorState,
      isOverLimit: charCount > limit,
    });
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  setMode: (mode) => {
    const { state, captionLimit } = get();
    const limit = mode === 'message' ? MAX_MESSAGE_LENGTH : captionLimit;
    set({
      mode,
      isOverLimit: state ? state.charCount > limit : false,
    });
  },

  setCaptionLimit: (captionLimit) => {
    const { state, mode } = get();
    const limit = mode === 'message' ? MAX_MESSAGE_LENGTH : captionLimit;
    set({
      captionLimit,
      isOverLimit: state ? state.charCount > limit : false,
    });
  },

  setChannelName: (name) => {
    set({ channelName: name });
  },

  setMediaPreview: (items) => {
    set({ mediaPreview: items });
  },

  setLinkPreviewEnabled: (enabled) => {
    set({ linkPreviewEnabled: enabled });
  },

  clear: () => {
    set({
      state: null,
      editorState: null,
      isOverLimit: false,
      mediaPreview: [],
    });
  },
}));
